import { OWObject } from "OW.Remoting/Remoting/DataTypes/OWObject";
import { FormGroupContainer } from "UI/UI/DefaultInterface/FormGroupContainer";
import { Event } from "../../../../Global/Base/Events";
import { IDisposable } from "../../../../Global/Base/IDisposable";
import { IFRMParams } from "../Controls/FormParameters";
import { IWindowable } from "../UIManagerInterfaces";
import { FormResult } from "../XGControls/Forms/FormResult";
import { IForm } from "../XGControls/IForm";
import { ModalWindow, ModalWindowType } from "../XGControls/ModalWindow";

/**
 * Položka FormManageru, obsahuje kolekci otevřených formulářů skládající se z hlavního formuláře a všech jeho podřízených
 * @includeToDoc
 */
export interface IFormGroup extends IDisposable {
    /** Událost volaná při otevření podřízeného formuláře */
    readonly formOpened: Event<IFormGroup, FormEvent>;
    /** Událost volaná při zavření podřízeného formuláře */
    readonly formClosed: Event<IFormGroup, FormEvent>;
    /** Událost volána při změně viditelnosti skupiny formulářů. */
    readonly formGroupVisibilityChanged: Event<IFormGroup, FormEventVisibilityChanged>;
    /** Událost volána při změně viditelnosti samotného formuláře. */
    readonly formVisibilityChanged: Event<IFormGroup, FormEventVisibilityChanged>;

    /** Název hlavního formuláře */
    readonly formName: string;
    /** identifikator otevřeného formuláře, který se naplní po otevření pro jednoznačnou identifikaci */
    readonly formInstanceId: string;
    /** Odkaz na formulář */
    readonly form: IWindowable;
    /** Aktualni modalni okno */
    readonly currentModal: ModalWindow;
    /** Kontejner obsahujici hlavni formular */
    readonly container: FormGroupContainer;

    /**
     * Otevře vlastní instanci formuláře
     *
     * @param form Vlastní form, který má byt zobrazen
     * @param init Nepovinná inicializační metoda, které je předáno aktuální IFormUI
     */
    openCustomModalForm<F extends IWindowable>(form: F): Promise<F>;

    /** Otevře nové modální okno */
    openForm(param: IFormGroupOpenModalParams): Promise<IForm>;
    /** Otevře hlavní formulář */
    openMainForm(): Promise<void>;
    /** Zavře celou skupinu */
    close(): Promise<void>;

    /** Zobrazí celou skupinu */
    show(): void;
    /** Skryje celou skupinu */
    hide(): void;
}

export class IFormGroup {
    private static _properties: (keyof (IFormGroup))[] = ["formOpened", "formClosed", "formVisibilityChanged", "formGroupVisibilityChanged", "currentModal", "formName", "formInstanceId", "form", "container"];
    private static _methods: (keyof (IFormGroup))[] = ["openCustomModalForm", "openForm", "openMainForm", "close", "show", "hide"];
    public static [Symbol.hasInstance](instance: any): boolean {
        return instance != null && !this._properties.some((value) => !Reflect.has(instance, value)) &&
            !this._methods.some(value => typeof instance[value] !== "function");
    }
}

export interface FormEventVisibilityChanged extends FormEvent {
    isVisible: boolean;
}

export interface FormEvent {
    parentForm: IWindowable;
    form: IWindowable;
}

interface IFormGroupOpenModalParamsData {
    parametricData?: IFRMParams;
    onClose?: (sender?: IWindowable, result?: FormResult) => void;
    type?: ModalWindowType;
    showBreadcrumbNav?: boolean;
    parentFormId?: string;
}

interface IFormGroupOpenModalParamsFormName extends IFormGroupOpenModalParamsData {
    formName: string;
}

interface IFormGroupOpenModalParamsIForm<F extends IWindowable> extends IFormGroupOpenModalParamsData {
    form: F;
}

/** Parametry pro otevření modálního okna s detailem objektu ve vazbě. */
export interface IFormGroupOpenModalAssociationParams extends IFormGroupOpenModalParamsFormName {
    object: OWObject;
    attrName: string;
}

export type IFormGroupOpenModalParams = IFormGroupOpenModalParamsFormName | IFormGroupOpenModalParamsIForm<IWindowable>;
